
import { supabase } from './client';
import { checkTableExists } from './queryUtils';

/**
 * Get the current user's role
 * @returns Promise with the user's role
 */
export const getUserRole = async (): Promise<string> => {
  try {
    // First, try to get role from the current session
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (sessionData?.session?.user?.app_metadata?.role) {
      return sessionData.session.user.app_metadata.role;
    }
    
    // If no role in session, try to get it from the database
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return 'guest';
    }
    
    // Check if the user_roles table exists
    const tableExists = await checkTableExists('user_roles');
    
    if (tableExists) {
      // Try using the get_current_user_role function if it exists
      try {
        const { data: roleData, error: roleError } = await supabase.rpc('get_current_user_role');
        
        if (!roleError && roleData) {
          return roleData;
        }
      } catch (error) {
        // Function might not exist, continue with direct query
        console.error('Error getting role from RPC function:', error);
      }
      
      // Direct query as fallback using RPC
      const { data: roleData, error: roleError } = await supabase
        .rpc('get_user_role', { user_id_param: user.id });
      
      if (!roleError && roleData) {
        return roleData;
      }
    }
    
    // Final fallback
    return 'user';
  } catch (error) {
    console.error('Error getting user role:', error);
    return 'user';
  }
};

/**
 * Setup auth listener to track role changes
 * @param onRoleChange Callback function to handle role changes
 * @returns Unsubscribe function
 */
export const setupAuthListener = (onRoleChange: (role: string) => void) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      const role = session?.user?.app_metadata?.role || await getUserRole();
      onRoleChange(role);
    } else if (event === 'SIGNED_OUT') {
      onRoleChange('guest');
    }
  });
  
  return () => {
    subscription.unsubscribe();
  };
};
